import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Clock, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatTimeRange12h, normalizeTime24 } from '../utils/timeFormat';
import TimeInput12h from './ui/time-input-12h';
import TeacherDayCalendar from './timetable/TeacherDayCalendar';
import SchoolHoursSchedule from './timetable/SchoolHoursSchedule';
import AbsentNotificationSchedule from './timetable/AbsentNotificationSchedule';
import useBranchLabels from '../hooks/useBranchLabels';

const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getDefaultWeekday = () => {
  const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const today = map[new Date().getDay()];
  return weekDays.includes(today) ? today : 'monday';
};

const Timetable = () => {
  const { user, canManageFeature } = useAuth();
  const { formatLabel } = useBranchLabels();
  const canManageTimetable = canManageFeature('timetable');
  const canManageScheduleTabs = canManageTimetable && ['school_admin', 'principal', 'super_admin'].includes(user?.role_slug);
  const [moduleTab, setModuleTab] = useState('periods');
  const [timetable, setTimetable] = useState({ entries: [], grouped: {} });
  const [classes, setClasses] = useState([]);
  const [formSubjects, setFormSubjects] = useState([]);
  const [editSubjects, setEditSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [viewMode, setViewMode] = useState('class');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [teacherSelectedDay, setTeacherSelectedDay] = useState(getDefaultWeekday);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    class_id: '', subject_id: '', teacher_id: '', section_id: '', day_of_week: '', start_time: '', end_time: '', room: '',
  });
  /** all_weekdays = Mon–Sat slots with same time/subject/teacher; single_day = one day picker */
  const [addDayMode, setAddDayMode] = useState('single_day');
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editForm, setEditForm] = useState({
    subject_id: '', teacher_id: '', section_id: '', day_of_week: '', start_time: '', end_time: '', room: '',
  });

  useEffect(() => { fetchData(); }, [user?.school_id]);
  useEffect(() => {
    if (viewMode === 'class' && selectedClass) fetchTimetable();
    else if (viewMode === 'teacher' && selectedTeacher) fetchTimetable();
    else setTimetable({ entries: [], grouped: {} });
  }, [viewMode, selectedClass, selectedTeacher, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchData();
      refreshTimetable();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [viewMode, selectedClass, selectedTeacher, user?.school_id]);

  const fetchData = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const classParams = academicYearId ? { academic_year_id: academicYearId } : {};
      const classesRes = await api.get('/classes', { params: classParams });
      const classList = classesRes.data.data || [];
      setClasses(classList);
      if (!selectedClass && classList.length > 0) {
        setSelectedClass(String(classList[0].id));
      } else if (selectedClass && !classList.some((c) => String(c.id) === String(selectedClass))) {
        setSelectedClass(classList.length > 0 ? String(classList[0].id) : '');
      }
      const schoolId = user?.school_id;
      const teachersRes = await api.get('/teachers', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      const teacherList = teachersRes.data.data || [];
      setTeachers(teacherList);
      if (selectedTeacher && !teacherList.some((t) => String(t.id) === String(selectedTeacher))) {
        setSelectedTeacher('');
      }
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const canFetchTimetable = () => {
    if (viewMode === 'class') return Boolean(selectedClass);
    if (viewMode === 'teacher') return Boolean(selectedTeacher);
    return false;
  };

  const refreshTimetable = () => {
    if (canFetchTimetable()) fetchTimetable();
    else setTimetable({ entries: [], grouped: {} });
  };

  const fetchClassSubjects = async (classId, legacySubject = null) => {
    if (!classId) return [];
    const academicYearId = getSelectedAcademicYearId();
    const params = academicYearId ? { academic_year_id: academicYearId } : {};
    try {
      const res = await api.get(`/classes/${classId}/subjects`, { params });
      let list = res.data?.data ?? [];
      if (legacySubject?.id && !list.some((s) => String(s.id) === String(legacySubject.id))) {
        list = [
          ...list,
          {
            id: legacySubject.id,
            name: legacySubject.name || `Subject #${legacySubject.id}`,
            code: legacySubject.code,
          },
        ];
      }
      return list;
    } catch {
      return legacySubject?.id
        ? [{ id: legacySubject.id, name: legacySubject.name || `Subject #${legacySubject.id}` }]
        : [];
    }
  };

  useEffect(() => {
    if (!canManageTimetable || !formData.class_id) {
      setFormSubjects([]);
      return;
    }
    let cancelled = false;
    fetchClassSubjects(formData.class_id).then((list) => {
      if (!cancelled) {
        setFormSubjects(list);
        setFormData((prev) => {
          if (!prev.subject_id || list.some((s) => String(s.id) === String(prev.subject_id))) {
            return prev;
          }
          return { ...prev, subject_id: '' };
        });
      }
    });
    return () => { cancelled = true; };
  }, [formData.class_id, canManageTimetable, user?.school_id]);

  useEffect(() => {
    if (!canManageTimetable || !editDialogOpen || !editingEntry?.class_id) {
      setEditSubjects([]);
      return;
    }
    let cancelled = false;
    const legacySubject = editingEntry.subject_id
      ? { id: editingEntry.subject_id, name: editingEntry.subject_name }
      : null;
    fetchClassSubjects(editingEntry.class_id, legacySubject).then((list) => {
      if (!cancelled) setEditSubjects(list);
    });
    return () => { cancelled = true; };
  }, [editDialogOpen, editingEntry?.class_id, editingEntry?.subject_id, editingEntry?.subject_name, canManageTimetable, user?.school_id]);

  const fetchTimetable = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      if (viewMode === 'class') {
        if (!selectedClass) return;
        params.class_id = selectedClass;
      } else if (viewMode === 'teacher') {
        if (!selectedTeacher) return;
        params.teacher_id = selectedTeacher;
      } else {
        return;
      }
      const response = await api.get('/timetable', { params });
      setTimetable(response.data.data || { entries: [], grouped: {} });
    } catch (error) {
      console.error('Failed to fetch timetable');
    }
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      subject_id: String(entry.subject_id ?? ''),
      teacher_id: String(entry.teacher_id ?? ''),
      section_id: entry.section_id != null ? String(entry.section_id) : '',
      day_of_week: entry.day_of_week || '',
      start_time: normalizeTime24(entry.start_time),
      end_time: normalizeTime24(entry.end_time),
      room: entry.room || '',
    });
    setEditDialogOpen(true);
  };

  const isEndAfterStart = (start, end) => {
    if (!start || !end) return true;
    return start < end;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntry?.id) return;
    if (!editForm.subject_id || !editForm.teacher_id || !editForm.day_of_week) {
      toast({ title: 'Validation Error', description: 'Subject, Teacher and Day are required', variant: 'destructive' });
      return;
    }
    if (editSubjects.length === 0) {
      toast({ title: 'Validation Error', description: 'No subjects have been assigned to this class yet.', variant: 'destructive' });
      return;
    }
    if (!isEndAfterStart(editForm.start_time, editForm.end_time)) {
      toast({ title: 'Validation Error', description: 'End time must be after start time', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        subject_id: parseInt(editForm.subject_id, 10),
        teacher_id: parseInt(editForm.teacher_id, 10),
        day_of_week: editForm.day_of_week,
        start_time: editForm.start_time || undefined,
        end_time: editForm.end_time || undefined,
        room: editForm.room || undefined,
      };
      if (editForm.section_id) {
        payload.section_id = parseInt(editForm.section_id, 10);
      } else {
        payload.section_id = null;
      }
      await api.put(`/timetable/${editingEntry.id}`, payload);
      toast({ title: 'Success', description: 'Timetable updated' });
      setEditDialogOpen(false);
      setEditingEntry(null);
      refreshTimetable();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update', variant: 'destructive' });
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm('Delete this timetable period?')) return;
    try {
      await api.delete(`/timetable/${entry.id}`);
      toast({ title: 'Success', description: 'Period removed' });
      refreshTimetable();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const resetAddForm = () => {
    setFormData({ class_id: '', subject_id: '', teacher_id: '', section_id: '', day_of_week: '', start_time: '', end_time: '', room: '' });
    setAddDayMode('single_day');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.class_id || !formData.subject_id || !formData.teacher_id || !formData.start_time || !formData.end_time) {
      toast({ title: 'Validation Error', description: 'Class, Subject, Teacher, Start and End time are required', variant: 'destructive' });
      return;
    }
    if (formSubjects.length === 0) {
      toast({ title: 'Validation Error', description: 'No subjects have been assigned to this class yet.', variant: 'destructive' });
      return;
    }
    if (!isEndAfterStart(formData.start_time, formData.end_time)) {
      toast({ title: 'Validation Error', description: 'End time must be after start time', variant: 'destructive' });
      return;
    }
    const daysToCreate = addDayMode === 'all_weekdays' ? weekDays : (formData.day_of_week ? [formData.day_of_week] : []);
    if (daysToCreate.length === 0) {
      toast({ title: 'Validation Error', description: 'Select a day (day-wise mode)', variant: 'destructive' });
      return;
    }
    const academicYearId = getSelectedAcademicYearId();
    const base = {
      class_id: parseInt(formData.class_id, 10),
      subject_id: parseInt(formData.subject_id, 10),
      teacher_id: parseInt(formData.teacher_id, 10),
      start_time: formData.start_time,
      end_time: formData.end_time,
      room: formData.room || undefined,
      academic_year_id: academicYearId || undefined,
    };
    if (formData.section_id) {
      base.section_id = parseInt(formData.section_id, 10);
    }

    setAddSubmitting(true);
    let ok = 0;
    const errors = [];
    try {
      for (const day of daysToCreate) {
        try {
          await api.post('/timetable', { ...base, day_of_week: day });
          ok += 1;
        } catch (err) {
          errors.push(`${day}: ${err.response?.data?.message || err.message || 'failed'}`);
        }
      }
      if (ok === daysToCreate.length) {
        toast({
          title: 'Success',
          description: addDayMode === 'all_weekdays' ? `Added ${ok} periods (Mon–Sat)` : 'Timetable entry created',
        });
        setIsDialogOpen(false);
        resetAddForm();
        refreshTimetable();
      } else if (ok > 0) {
        toast({
          title: 'Partial success',
          description: `${ok}/${daysToCreate.length} created. ${errors.slice(0, 2).join('; ')}`,
          variant: 'destructive',
        });
        refreshTimetable();
      } else {
        toast({
          title: 'Error',
          description: errors[0] || 'Failed to create entries',
          variant: 'destructive',
        });
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const editClassObj = editingEntry
    ? classes.find((c) => String(c.id) === String(editingEntry.class_id))
    : null;
  const editSectionOptions = editClassObj?.sections || [];

  const handleClassChange = (classId) => {
    setFormData((prev) => ({
      ...prev,
      class_id: classId,
      subject_id: '',
      section_id: '',
    }));
  };

  const openAddDialog = (open) => {
    setIsDialogOpen(open);
    if (open) {
      setFormData((prev) => ({
        ...prev,
        ...(viewMode === 'class' && selectedClass ? { class_id: selectedClass } : {}),
        ...(viewMode === 'teacher' && selectedTeacher ? { teacher_id: selectedTeacher } : {}),
      }));
    }
    if (!open) resetAddForm();
  };

  const renderPeriodSubtitle = (entry) => {
    const time = formatTimeRange12h(entry.start_time, entry.end_time);
    const section = entry.section_name ? ` · Sec ${entry.section_name}` : '';
    if (viewMode === 'teacher') {
      return `${time}${section} · ${entry.class_name || 'Class'}`;
    }
    return `${time}${section} · ${entry.teacher_first_name} ${entry.teacher_last_name || ''}`;
  };

  const showTimetableGrid = viewMode === 'class'
    ? Boolean(selectedClass)
    : Boolean(selectedTeacher);

  const emptyGridMessage = viewMode === 'class'
    ? 'Select a class to view timetable'
    : 'Select a teacher to view timetable';

  return (
    <div className="space-y-6" data-testid="timetable-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Timetable</h1>
          <p className="text-muted-foreground">
            {canManageScheduleTabs
              ? 'Manage period slots, school hours, and absent notification schedules.'
              : canManageTimetable
                ? "Manage class schedules or view a teacher's weekly schedule."
                : 'View class schedules or a teacher\'s weekly schedule.'}
          </p>
        </div>
        {canManageTimetable && moduleTab === 'periods' && (
        <Dialog
          open={isDialogOpen}
          onOpenChange={openAddDialog}
        >
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add Period</Button>
          </DialogTrigger>
          <DialogContent className="border-border text-foreground">
            <DialogHeader><DialogTitle>Add Timetable Entry</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Class *</Label>
                  <Select value={formData.class_id} onValueChange={handleClassChange}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">{classes.map(c => <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Subject *</Label>
                  <Select
                    value={formData.subject_id}
                    onValueChange={(v) => setFormData({ ...formData, subject_id: v })}
                    disabled={!formData.class_id || formSubjects.length === 0}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder={!formData.class_id ? 'Select a class first' : formSubjects.length === 0 ? 'No subjects assigned' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {formSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.id.toString()}>{formatLabel(s.name, s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.class_id && formSubjects.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-1">No subjects have been assigned to this class yet.</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Teacher (subject) *</Label>
                  <Select value={formData.teacher_id} onValueChange={(v) => setFormData({...formData, teacher_id: v})}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">{teachers.map(t => <SelectItem key={t.id} value={t.id.toString()}>{formatLabel(`${t.first_name} ${t.last_name}`.trim(), t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Section</Label>
                  <Select value={formData.section_id || 'all'} onValueChange={(v) => setFormData({ ...formData, section_id: v === 'all' ? '' : v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="All sections" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="all">All sections</SelectItem>
                      {(classes.find((c) => String(c.id) === String(formData.class_id))?.sections || []).map((sec) => (
                        <SelectItem key={sec.id} value={String(sec.id)}>Section {sec.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Schedule *</Label>
                  <Select value={addDayMode} onValueChange={(v) => setAddDayMode(v)}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="single_day">Day wise (one day)</SelectItem>
                      <SelectItem value="all_weekdays">All days (Mon–Sat, same slot)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {addDayMode === 'single_day' ? (
                  <div><Label>Day *</Label>
                    <Select value={formData.day_of_week} onValueChange={(v) => setFormData({ ...formData, day_of_week: v })}>
                      <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select day" /></SelectTrigger>
                      <SelectContent className="bg-muted border-border">{weekDays.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-end pb-2">
                    <p className="text-sm text-muted-foreground">Same period copied to Monday through Saturday.</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Start Time *</Label><TimeInput12h value={formData.start_time} onChange={(v) => setFormData({...formData, start_time: v})} triggerClassName="bg-muted border-border" required /></div>
                <div><Label>End Time *</Label><TimeInput12h value={formData.end_time} onChange={(v) => setFormData({...formData, end_time: v})} triggerClassName="bg-muted border-border" required /></div>
                <div><Label>Room</Label><Input value={formData.room} onChange={(e) => setFormData({...formData, room: e.target.value})} className="bg-muted border-border" /></div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={addSubmitting}>
                {addSubmitting ? 'Saving…' : addDayMode === 'all_weekdays' ? `Add ${weekDays.length} periods` : 'Add period'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}

        {canManageTimetable && (
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingEntry(null); }}>
          <DialogContent className="border-border text-foreground max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit period</DialogTitle></DialogHeader>
            {editingEntry && (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {editingEntry.class_name} {editingEntry.section_name ? `· Section ${editingEntry.section_name}` : ''}
                </p>
                <div><Label>Subject *</Label>
                  <Select
                    value={editForm.subject_id}
                    onValueChange={(v) => setEditForm({ ...editForm, subject_id: v })}
                    disabled={editSubjects.length === 0}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder={editSubjects.length === 0 ? 'No subjects assigned' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {editSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.id.toString()}>{formatLabel(s.name, s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editSubjects.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-1">No subjects have been assigned to this class yet.</p>
                  )}
                </div>
                <div><Label>Teacher (subject) *</Label>
                  <Select value={editForm.teacher_id} onValueChange={(v) => setEditForm({ ...editForm, teacher_id: v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">{teachers.map(t => <SelectItem key={t.id} value={t.id.toString()}>{formatLabel(`${t.first_name} ${t.last_name}`.trim(), t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Section</Label>
                  <Select value={editForm.section_id || 'all'} onValueChange={(v) => setEditForm({ ...editForm, section_id: v === 'all' ? '' : v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="All sections" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="all">All sections</SelectItem>
                      {editSectionOptions.map((sec) => (
                        <SelectItem key={sec.id} value={String(sec.id)}>Section {sec.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Day *</Label>
                  <Select value={editForm.day_of_week} onValueChange={(v) => setEditForm({ ...editForm, day_of_week: v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">{weekDays.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Start</Label><TimeInput12h value={editForm.start_time} onChange={(v) => setEditForm({ ...editForm, start_time: v })} triggerClassName="bg-muted border-border" /></div>
                  <div><Label>End</Label><TimeInput12h value={editForm.end_time} onChange={(v) => setEditForm({ ...editForm, end_time: v })} triggerClassName="bg-muted border-border" /></div>
                  <div><Label>Room</Label><Input value={editForm.room} onChange={(e) => setEditForm({ ...editForm, room: e.target.value })} className="bg-muted border-border" /></div>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Save changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>

      {canManageScheduleTabs && (
        <Tabs value={moduleTab} onValueChange={setModuleTab}>
          <TabsList className="flex flex-wrap h-auto w-full sm:w-auto">
            <TabsTrigger value="periods">Period Timetable</TabsTrigger>
            <TabsTrigger value="school-hours">School Hours</TabsTrigger>
            <TabsTrigger value="absent-notifications">Absent Notifications</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {moduleTab === 'school-hours' && canManageScheduleTabs ? (
        <SchoolHoursSchedule classes={classes} />
      ) : moduleTab === 'absent-notifications' && canManageScheduleTabs ? (
        <AbsentNotificationSchedule />
      ) : (
      <>
      {/* View mode & filter */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-4">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="flex flex-wrap h-auto w-full sm:w-auto">
              <TabsTrigger value="class">By Class</TabsTrigger>
              <TabsTrigger value="teacher">By Teacher</TabsTrigger>
            </TabsList>
          </Tabs>
          {viewMode === 'class' ? (
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-full sm:w-48 bg-muted border-border text-foreground"><SelectValue placeholder="Select Class" /></SelectTrigger>
              <SelectContent className="bg-muted border-border">{classes.map(c => <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
              <SelectTrigger className="w-full sm:w-64 bg-muted border-border text-foreground"><SelectValue placeholder="Select teacher" /></SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {teachers.map(t => (
                  <SelectItem key={t.id} value={t.id.toString()}>{formatLabel(`${t.first_name} ${t.last_name}`.trim(), t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Timetable Grid */}
      {viewMode === 'class' && classes.length === 0 ? (
        <Card className="border-border"><CardContent className="py-12 text-center"><p className="text-muted-foreground">No classes assigned to you</p></CardContent></Card>
      ) : !showTimetableGrid ? (
        <Card className="border-border"><CardContent className="py-12 text-center"><p className="text-muted-foreground">{emptyGridMessage}</p></CardContent></Card>
      ) : viewMode === 'teacher' ? (
        <TeacherDayCalendar
          entries={timetable.grouped[teacherSelectedDay] || []}
          selectedDay={teacherSelectedDay}
          onDayChange={setTeacherSelectedDay}
          weekDays={weekDays}
          canManage={canManageTimetable}
          onEdit={openEdit}
          onDelete={handleDeleteEntry}
        />
      ) : (
        <div className="grid gap-4">
          {weekDays.map(day => (
            <Card key={day} className="border-border">
              <CardHeader className="py-3">
                <CardTitle className="text-foreground capitalize">{day}</CardTitle>
              </CardHeader>
              <CardContent>
                {(timetable.grouped[day] || []).length === 0 ? (
                  <p className="text-muted-foreground text-sm">No classes scheduled</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {(timetable.grouped[day] || []).map(entry => (
                      <div key={entry.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                          <Clock className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground">{entry.subject_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {renderPeriodSubtitle(entry)}
                          </p>
                        </div>
                        {canManageTimetable && (
                          <div className="flex gap-1 shrink-0">
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-foreground" onClick={() => openEdit(entry)} aria-label="Edit">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDeleteEntry(entry)} aria-label="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Timetable;
